import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {ticketStatusCategoryColors} from '../../../../../../libs/inboxes/ui/src/ticketing/ticketStatus';
import {TicketStatusName} from '../../../../../../libs/inboxes/ui/src/ticketing/ticketStatusName';
import {TimeFormatsEnum} from '../../../../../../libs/shared/core/src/helpers/datetime/dateTimeHelpers';
import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {UnreachableError} from '../../../../../../libs/shared/core/src/helpers/types/customErrors';
import {InboxModel} from '../../../../../../libs/shared/core/src/models/api/inboxModel';
import {PaletteColorsEnum} from '../../../../../../libs/shared/core/src/models/api/paletteModel';
import {TicketStatusModel} from '../../../../../../libs/shared/core/src/models/api/ticketStatusModel';
import {CellsContext} from '../../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {
  BannerButton,
  BannerButtonColorVariantsEnum,
  bannerButtonSizeConstants,
} from '../../../../../../libs/shared/ui/src/components/buttons/bannerButton';
import {AbsoluteDateTimeFormatter} from '../../../../../../libs/shared/ui/src/components/datetime/absoluteDateTimeFormatter';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {
  StaticViewerActivity,
  StaticViewerActivityDetails,
  StaticViewerActivityTypesEnum,
} from '../../../../../../srcCommon/inboxes/viewer/activity/static/staticViewerHelpers';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {ReminderModel} from '../../../../../models/api/reminderModel';
import {TicketStatusIndicator} from '../../../ticketing/ticketStatusIndicator';
import {ConversationTicketStatusCoordinator} from '../../conversation/actions/ticketStatus/conversationTicketStatusCoordinator';
import {StaticViewerBanner} from './staticViewerBanner';

/*
 * Props.
 */

export interface StaticViewerProps {
  /* Context to perform team actions, even if P3 is not in a team/assignee context. */
  teamCellsContext: CellsContext | undefined;
  conversation: ConversationModel;
  teamInboxes: ReadonlyArray<InboxModel>;
  timeFormat: TimeFormatsEnum;
  teamReminder: ReminderModel | undefined;
  ticketStatus: TicketStatusModel | undefined;
  staticViewerActivity: StaticViewerActivity;
  staticViewerActivityDetails: StaticViewerActivityDetails;
}

/*
 * Style.
 */

const StyledStaticViewerSpan = styled.span`
  color: ${(p) => p.theme.greys.shade90};
`;

const bannerButtonSize = VisualSizesEnum.SMALL;

const StyledTicketStatusCoordinator = styled(ConversationTicketStatusCoordinator)`
  margin-top: ${-bannerButtonSizeConstants[bannerButtonSize].paddingVertical}px;
  margin-right: ${-bannerButtonSizeConstants[bannerButtonSize].paddingHorizontal}px;
  margin-bottom: ${-bannerButtonSizeConstants[bannerButtonSize].paddingVertical}px;
`;

/*
 * Component.
 */

export const StaticViewer: FC<StaticViewerProps> = (props) => {
  const {
    teamCellsContext,
    conversation,
    teamInboxes,
    timeFormat,
    teamReminder,
    ticketStatus,
    staticViewerActivity,
    staticViewerActivityDetails: {teammateName, date},
  } = props;

  const [isTicketStatusDropdownOpen, setIsTicketStatusDropdownOpen] = useState(false);

  // If this is not a team conversation, nothing to do.
  if (teamInboxes.length === 0) {
    return null;
  }

  const {type} = staticViewerActivity;

  switch (type) {
    case StaticViewerActivityTypesEnum.TICKET_STATUS: {
      if (!ticketStatus || !teamCellsContext) {
        return null;
      }

      const body = teammateName ? (
        <Trans id="activity-static-ticket-status-with-teammate">
          <strong>
            <TicketStatusName tagId={ticketStatus.tagId} />
          </strong>{' '}
          in {renderInboxName(teamInboxes)} by {teammateName}
        </Trans>
      ) : (
        <Trans id="activity-static-ticket-status-without-teammate">
          <strong>
            <TicketStatusName tagId={ticketStatus.tagId} />
          </strong>{' '}
          in {renderInboxName(teamInboxes)}
        </Trans>
      );

      return (
        <StaticViewerBanner
          icon={<TicketStatusIndicator category={ticketStatus.category} isDarker />}
          color={ticketStatusCategoryColors[ticketStatus.category]}
          date={date}
          action={
            <StyledTicketStatusCoordinator
              cellsContext={teamCellsContext}
              conversations={[conversation]}
              placement="bottom-start"
              renderButton={({isOpen, isDisabled}) => (
                <BannerButton
                  hasHover={isOpen}
                  isDisabled={isDisabled}
                  size={bannerButtonSize}
                  iconName="chevronDownSmall"
                  colorVariant={BannerButtonColorVariantsEnum.DARK_ON_LIGHT}
                >
                  <Trans id="staticviewer-ticketstatus-update">Change status</Trans>
                </BannerButton>
              )}
              isTicketStatusDropdownOpen={isTicketStatusDropdownOpen}
              onTicketStatusDropdownToggle={setIsTicketStatusDropdownOpen}
            />
          }
        >
          <StyledStaticViewerSpan>{body}</StyledStaticViewerSpan>
        </StaticViewerBanner>
      );
    }
    case StaticViewerActivityTypesEnum.REMINDER: {
      if (!teamReminder) {
        return null;
      }

      const snoozedBody = teammateName ? (
        <Trans id="activity-static-snoozed-with-teammate">
          <strong>
            Snoozed until <AbsoluteDateTimeFormatter value={teamReminder.remindAt} timeFormat={timeFormat} />
          </strong>{' '}
          in {renderInboxName(teamInboxes)} by {teammateName}
        </Trans>
      ) : (
        <Trans id="activity-static-snoozed-without-teammate">
          <strong>
            Snoozed until <AbsoluteDateTimeFormatter value={teamReminder.remindAt} timeFormat={timeFormat} />
          </strong>{' '}
          in {renderInboxName(teamInboxes)}
        </Trans>
      );

      return (
        <StaticViewerBanner iconName="snoozeFilledSmall" color={PaletteColorsEnum.ORANGE} date={date}>
          <StyledStaticViewerSpan>{snoozedBody}</StyledStaticViewerSpan>
        </StaticViewerBanner>
      );
    }
    case StaticViewerActivityTypesEnum.OPEN: {
      const openBody = teammateName ? (
        <Trans id="activity-static-open-with-teammate">
          <strong>Open</strong> in {renderInboxName(teamInboxes)} by {teammateName}
        </Trans>
      ) : (
        <Trans id="activity-static-open-without-teammate">
          <strong>Open</strong> in {renderInboxName(teamInboxes)}
        </Trans>
      );

      return (
        <StaticViewerBanner iconName="inboxFilledSmall" color={PaletteColorsEnum.BLUE} date={date}>
          <StyledStaticViewerSpan>{openBody}</StyledStaticViewerSpan>
        </StaticViewerBanner>
      );
    }
    case StaticViewerActivityTypesEnum.ARCHIVED: {
      const archivedBody = teammateName ? (
        <Trans id="activity-static-archived-with-teammate">
          <strong>Archived</strong> in {renderInboxName(teamInboxes)} by {teammateName}
        </Trans>
      ) : (
        <Trans id="activity-static-archived-without-teammate">
          <strong>Archived</strong> in {renderInboxName(teamInboxes)}
        </Trans>
      );

      return (
        <StaticViewerBanner iconName="archiveFilledSmall" color={PaletteColorsEnum.GREEN} date={date}>
          <StyledStaticViewerSpan>{archivedBody}</StyledStaticViewerSpan>
        </StaticViewerBanner>
      );
    }
    case StaticViewerActivityTypesEnum.TRASHED: {
      const trashedBody = teammateName ? (
        <Trans id="activity-static-trashed-with-teammate">
          <strong>Trashed</strong> in {renderInboxName(teamInboxes)} by {teammateName}
        </Trans>
      ) : (
        <Trans id="activity-static-trashed-without-teammate">
          <strong>Trashed</strong> in {renderInboxName(teamInboxes)}
        </Trans>
      );

      return (
        <StaticViewerBanner iconName="trashFilledSmall" color={PaletteColorsEnum.RED} date={date}>
          <StyledStaticViewerSpan>{trashedBody}</StyledStaticViewerSpan>
        </StaticViewerBanner>
      );
    }
    default:
      throw new UnreachableError(type);
  }
};

/*
 * Helpers.
 */

function renderInboxName(inboxes: ReadonlyArray<InboxModel>) {
  // Multiple inboxes: generic text.
  if (inboxes.length !== 1) {
    return <Trans id="activity-static-multipleinboxes">{inboxes.length} inboxes</Trans>;
  }

  // Otherwise, inbox name.
  return inboxes[0].name;
}
